import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import TaskDetailModal from '../components/TaskDetailModal';
import { supabase, TABLES } from '../api/db';
import { Task, AIAnalysis, TaskerProfile, ConnectionRequest } from '../types';
import {
  Search, Clock, Eye, MessageCircle,
  TrendingUp, Send, Loader, CheckCircle, XCircle, ExternalLink, Zap,
  SlidersHorizontal, X, Globe, Filter, Fingerprint, Flame, Sparkles,
} from 'lucide-react';
import {
  castVote,
  clearVote,
  getRatingSummaries,
  getMyVotes,
  onPointScore,
  type RatingVote,
  type RatingSummary,
} from '../api/taskRatings';
import { useAuth } from '../contexts/AuthContext';
import { sendConnectionRequest, checkConnectionRequest } from '../api/connections';
import { notifyConnectionRequest } from '../api/notifications';
import { getTaskerProfileByUserId } from '../api/taskerProfiles';
import { TASK_UNIVERSES, EXPERIENCE_LEVELS, TIMELINES, COLLAB_STYLES, type TaskUniverse } from '../constants/taskCreation';
import { isValidTaskCode, normalizeTaskCode } from '../utils/taskCodeGenerator';

interface TaskWithAnalysis extends Task {
  aiAnalysis?: AIAnalysis;
  matchScore?: number;
  universe?: TaskUniverse;
  experience?: string;
  timeline?: string;
  collabStyle?: string;
  tools?: { id: string; name: string; category: string }[];
  goal?: string;
}

const TASKS_PER_PAGE = 12;

function computeMatchScore(task: Task, profile: TaskerProfile | null): number {
  if (!profile) return 0;
  const s = profile.skills;
  const allSkills = [...(s?.primary || []), ...(s?.secondary || [])];
  const profileSkills = allSkills.map((v) => v.toLowerCase());
  if (profileSkills.length === 0) return 0;
  const taskTags = (task.tags || []).map((t) => t.toLowerCase());
  if (taskTags.length === 0) return 0;
  const overlap = taskTags.filter((t) => profileSkills.some((sk) => sk.includes(t) || t.includes(sk))).length;
  return Math.min(Math.round((overlap / taskTags.length) * 100), 100);
}

function scoreColor(score: number): string {
  if (score >= 70) return 'text-emerald-300 border-emerald-400/50 bg-emerald-500/15';
  if (score >= 40) return 'text-amber-300 border-amber-400/50 bg-amber-500/15';
  return 'text-purple-300 border-purple-400/30 bg-purple-500/10';
}

const TaskMarketplace = () => {
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTaskId = searchParams.get('task');
  const [tasks, setTasks] = useState<TaskWithAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [, setLastDoc] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [, setPage] = useState(1);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterUniverse, setFilterUniverse] = useState<TaskUniverse | 'all'>('all');
  const [filterExperience, setFilterExperience] = useState<string>('all');
  const [filterTimeline, setFilterTimeline] = useState<string>('all');
  const [filterCollab, setFilterCollab] = useState<string>('all');

  const [myProfile, setMyProfile] = useState<TaskerProfile | null>(null);
  const [connectionStates, setConnectionStates] = useState<
    Record<string, { status: 'idle' | 'composing' | 'sending' | 'sent' | 'exists'; existing?: ConnectionRequest }>
  >({});
  const [draftMessages, setDraftMessages] = useState<Record<string, string>>({});
  const [directMatchTask, setDirectMatchTask] = useState<TaskWithAnalysis | null>(null);

  // On-Point vs Off-Topic rating state — optimistic local updates
  const [myVote, setMyVote]                 = useState<Record<string, RatingVote | undefined>>({});
  const [ratingSummaries, setRatingSummaries] = useState<Record<string, RatingSummary>>({});
  const [ratingBusy, setRatingBusy]         = useState<Record<string, boolean>>({});
  const [sortBy, setSortBy]                 = useState<'latest' | 'trending'>('latest');

  const activeFilterCount = [filterUniverse, filterExperience, filterTimeline, filterCollab].filter(f => f !== 'all').length;

  useEffect(() => {
    if (!currentUser) return;
    getTaskerProfileByUserId(currentUser.uid)
      .then((p) => setMyProfile(p))
      .catch(() => {});
  }, [currentUser]);

  useEffect(() => {
    loadTasks(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const checkExistingConnections = useCallback(
    async (taskList: TaskWithAnalysis[]) => {
      if (!currentUser) return;
      const updates: typeof connectionStates = {};
      await Promise.allSettled(
        taskList
          .filter((t) => t.userId !== currentUser.uid)
          .map(async (t) => {
            try {
              const existing = await checkConnectionRequest(t.id, currentUser.uid);
              if (existing) updates[t.id] = { status: 'exists', existing };
            } catch { /* ignore */ }
          })
      );
      if (Object.keys(updates).length > 0) {
        setConnectionStates((prev) => ({ ...prev, ...updates }));
      }
    },
    [currentUser]
  );

  /** Seed rating state (tallies + my vote) from the loaded tasks */
  const seedRatings = useCallback(async (taskList: TaskWithAnalysis[]) => {
    const ids = taskList.map((t) => t.id);
    const [summaries, mine] = await Promise.all([
      getRatingSummaries(ids),
      currentUser ? getMyVotes(ids, currentUser.uid) : Promise.resolve({} as Record<string, RatingVote>),
    ]);
    setRatingSummaries((prev) => ({ ...prev, ...summaries }));
    setMyVote((prev) => ({ ...prev, ...mine }));
  }, [currentUser]);

  /** Cast / switch / clear an On-Point or Off-Topic vote with optimistic UI */
  const handleVote = useCallback(async (taskId: string, vote: RatingVote) => {
    if (!currentUser || ratingBusy[taskId]) return;
    const prevVote = myVote[taskId];
    const nextVote: RatingVote | undefined = prevVote === vote ? undefined : vote;

    const applyDelta = (base: RatingSummary | undefined, from: RatingVote | undefined, to: RatingVote | undefined): RatingSummary => {
      const s = { onPoint: base?.onPoint ?? 0, offTopic: base?.offTopic ?? 0 };
      if (from === 'on_point') s.onPoint = Math.max(0, s.onPoint - 1);
      if (from === 'off_topic') s.offTopic = Math.max(0, s.offTopic - 1);
      if (to === 'on_point') s.onPoint += 1;
      if (to === 'off_topic') s.offTopic += 1;
      return s;
    };

    // Optimistic
    setMyVote((p) => ({ ...p, [taskId]: nextVote }));
    setRatingSummaries((p) => ({ ...p, [taskId]: applyDelta(p[taskId], prevVote, nextVote) }));
    setRatingBusy((p) => ({ ...p, [taskId]: true }));
    try {
      if (nextVote) await castVote(taskId, currentUser.uid, nextVote);
      else await clearVote(taskId, currentUser.uid);
    } catch {
      // Roll back
      setMyVote((p) => ({ ...p, [taskId]: prevVote }));
      setRatingSummaries((p) => ({ ...p, [taskId]: applyDelta(p[taskId], nextVote, prevVote) }));
    } finally {
      setRatingBusy((p) => ({ ...p, [taskId]: false }));
    }
  }, [currentUser, myVote, ratingBusy]);

  const loadTasks = async (reset = false) => {
    if (reset) { setLoading(true); setPage(1); setLastDoc(null); setHasMore(true); setDirectMatchTask(null); }
    else setLoadingMore(true);

    try {
      // --- Direct task code lookup: if query looks like a GNK code, pin exact match ---
      const trimmedQuery = searchQuery.trim();
      if (reset && trimmedQuery && isValidTaskCode(trimmedQuery)) {
        const normalizedCode = normalizeTaskCode(trimmedQuery);
        const { data: codeRows } = await supabase.from(TABLES.TASKS).select('*').eq('task_code', normalizedCode).limit(1);
        if (codeRows && codeRows.length > 0) {
          setDirectMatchTask({ ...(codeRows[0] as object) } as TaskWithAnalysis);
        }
      }

      const offset = reset ? 0 : tasks.length;
      let dbQuery = supabase
        .from(TABLES.TASKS)
        .select('*')
        .eq('visibility', 'public')
        .order('created_at', { ascending: false })
        .range(offset, offset + TASKS_PER_PAGE - 1);

      if (trimmedQuery && !isValidTaskCode(trimmedQuery)) {
        dbQuery = dbQuery.or(`title.ilike.%${trimmedQuery}%,summary.ilike.%${trimmedQuery}%`);
      }

      const { data: snapshot } = await dbQuery;
      const tasksData: TaskWithAnalysis[] = (snapshot ?? []).map((r) => ({ ...(r as object) } as TaskWithAnalysis));

      let filteredTasks = tasksData;
      if (trimmedQuery && !isValidTaskCode(trimmedQuery)) {
        const sl = trimmedQuery.toLowerCase();
        filteredTasks = tasksData.filter((task) => {
          const t = task.title?.toLowerCase().includes(sl);
          const s = task.summary?.toLowerCase().includes(sl);
          const g = (task as { goal?: string }).goal?.toLowerCase().includes(sl);
          const tg = task.tags?.some((tag) => tag.toLowerCase().includes(sl));
          const a = `${(task as { userName?: string }).userName || ''} ${(task as { userEmail?: string }).userEmail || ''}`.toLowerCase().includes(sl);
          const tools = (task as { tools?: { name: string }[] }).tools?.some?.((tool) => tool.name?.toLowerCase().includes(sl));
          const code = (task as { taskCode?: string }).taskCode?.toLowerCase().includes(sl);
          return t || s || g || tg || a || tools || code;
        });
      }

      if (reset) setTasks(filteredTasks);
      else setTasks((prev) => [...prev, ...filteredTasks]);

      if ((snapshot ?? []).length < TASKS_PER_PAGE) setHasMore(false);
      else setLastDoc(null); // Supabase uses range-based pagination

      void checkExistingConnections(filteredTasks);
      void seedRatings(filteredTasks);
    } catch (error: any) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) { setPage((prev) => prev + 1); loadTasks(false); }
  };

  const formatRelativeTime = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'Recently';
    const now = new Date();
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Recently';
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };


  const handleSendRequest = async (task: TaskWithAnalysis) => {
    if (!currentUser) return;
    setConnectionStates((p) => ({ ...p, [task.id]: { status: 'sending' } }));
    try {
      const reqId = await sendConnectionRequest(task.id, currentUser.uid, task.userId, draftMessages[task.id]?.trim() || undefined);
      try { await notifyConnectionRequest(task.userId, currentUser.uid, task.id, task.title, currentUser.displayName || 'Someone', reqId); } catch { /* optional */ }
      setConnectionStates((p) => ({ ...p, [task.id]: { status: 'sent' } }));
      setDraftMessages((p) => ({ ...p, [task.id]: '' }));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not send request.';
      alert(msg);
      setConnectionStates((p) => ({ ...p, [task.id]: { status: 'composing' } }));
    }
  };

  // Apply client-side filters + sort
  const filteredTasks = useMemo(() => {
    let result = tasks.map((t) => ({ ...t, matchScore: computeMatchScore(t, myProfile) }));
    if (filterUniverse !== 'all') {
      result = result.filter(t => {
        const taskUniverse = (t as any).universe || '';
        if (taskUniverse === filterUniverse) return true;
        const catMap: Record<string, string[]> = {
          create: ['graphic'], build: ['code'], design: ['graphic'], manage: ['IT', 'infrastructure', 'database', 'network', 'security'],
          automate: ['infrastructure'], market: ['other'], learn: ['other'], produce: ['graphic'],
          entertain: ['other'], brand: ['graphic'], research: ['other'],
        };
        return catMap[filterUniverse]?.includes(t.category) || false;
      });
    }
    if (filterExperience !== 'all') {
      result = result.filter(t => {
        const exp = (t as any).experience || (t as any).difficulty;
        const expMap: Record<string, string[]> = { explorer: ['beginner', 'explorer'], maker: ['intermediate', 'maker'], pro: ['advanced', 'pro'], expert: ['expert'] };
        return expMap[filterExperience]?.includes(exp) || false;
      });
    }
    if (filterTimeline !== 'all') {
      result = result.filter(t => {
        const tl = (t as any).timeline || '';
        if (tl === filterTimeline) return true;
        const urgMap: Record<string, string[]> = { asap: ['immediate', 'asap'], 'this-week': ['24h', 'this-week'], 'this-month': ['this-week', 'this-month'], 'no-rush': ['flexible', 'no-rush'] };
        return urgMap[filterTimeline]?.includes(t.urgency) || false;
      });
    }
    if (filterCollab !== 'all') {
      result = result.filter(t => (t as any).collabStyle === filterCollab);
    }
    // Sort
    if (sortBy === 'trending') {
      result = [...result].sort((a, b) => (ratingSummaries[b.id]?.onPoint ?? 0) - (ratingSummaries[a.id]?.onPoint ?? 0));
    }
    return result;
  }, [tasks, myProfile, filterUniverse, filterExperience, filterTimeline, filterCollab, sortBy, ratingSummaries]);

  const clearFilters = () => { setFilterUniverse('all'); setFilterExperience('all'); setFilterTimeline('all'); setFilterCollab('all'); };

  const resultCount = filteredTasks.length;

  return (
    <div className="min-h-screen bg-tech-surface text-tech-text bg-tech-mesh">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2.5 rounded-xl bg-tech-cyan/10 border border-tech-cyan/20">
              <Globe className="w-6 h-6 text-tech-cyan" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-tech-text">Task Marketplace</h1>
              <p className="text-xs sm:text-sm text-tech-text-muted mt-0.5">
                Discover what the global network is building, creating, and shipping
              </p>
            </div>
          </div>
        </div>

        {/* Search + Filter toggle */}
        <div className="flex flex-col sm:flex-row gap-3 mb-1">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-tech-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tasks, tools, creators, tags, or paste a GNK code..."
              className="w-full pl-10 pr-10 py-3 bg-tech-gray border border-tech-border rounded-xl text-sm text-tech-text placeholder-tech-text-muted/50 focus:outline-none focus:ring-2 focus:ring-tech-cyan/40 focus:border-tech-cyan transition-all"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-tech-text-muted hover:text-tech-text">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all shrink-0 ${
              showFilters || activeFilterCount > 0
                ? 'bg-tech-cyan/10 border-tech-cyan/30 text-tech-cyan shadow-tech-glow'
                : 'bg-tech-gray border-tech-border text-tech-text-muted hover:border-tech-cyan/30'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="w-5 h-5 rounded-full bg-tech-cyan text-tech-dark text-[10px] font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Search hint */}
        <p className="text-[11px] text-tech-text-muted/60 mb-3 pl-1 flex items-center gap-1.5">
          <Fingerprint className="w-3 h-3 text-tech-cyan/40" />
          Search by task name, tags, creator — or paste a <span className="font-mono text-tech-cyan/70">GNK-XXXX-XXXX</span> code for a direct match
        </p>

        {/* Filter panel */}
        {showFilters && (
          <div className="mb-6 p-5 rounded-2xl border border-tech-border/60 bg-tech-gray/40 backdrop-blur-sm space-y-5 animate-slide-up">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-tech-text flex items-center gap-2">
                <Filter className="w-4 h-4 text-tech-cyan" /> Filter Tasks
              </h3>
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} className="text-xs text-tech-cyan hover:text-tech-text transition-colors">
                  Clear all
                </button>
              )}
            </div>

            {/* Universe filter */}
            <div>
              <label className="text-xs font-semibold text-tech-text-muted mb-2 block uppercase tracking-wider">Universe</label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setFilterUniverse('all')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    filterUniverse === 'all' ? 'bg-tech-cyan/15 text-tech-cyan border border-tech-cyan/30' : 'bg-tech-gray text-tech-text-muted border border-transparent hover:border-tech-border'
                  }`}
                >All</button>
                {TASK_UNIVERSES.filter(u => u.id !== 'other').map(u => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => setFilterUniverse(u.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                      filterUniverse === u.id
                        ? 'border text-tech-text'
                        : 'bg-tech-gray text-tech-text-muted border border-transparent hover:border-tech-border'
                    }`}
                    style={filterUniverse === u.id ? { borderColor: u.color + '60', background: u.color + '15', color: u.color } : undefined}
                  >
                    <span>{u.emoji}</span> {u.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Experience filter */}
            <div>
              <label className="text-xs font-semibold text-tech-text-muted mb-2 block uppercase tracking-wider">Experience Level</label>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setFilterExperience('all')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterExperience === 'all' ? 'bg-tech-cyan/15 text-tech-cyan border border-tech-cyan/30' : 'bg-tech-gray text-tech-text-muted border border-transparent hover:border-tech-border'}`}
                >All</button>
                {EXPERIENCE_LEVELS.map(l => (
                  <button key={l.id} type="button" onClick={() => setFilterExperience(l.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${filterExperience === l.id ? 'bg-tech-cyan/15 text-tech-cyan border border-tech-cyan/30' : 'bg-tech-gray text-tech-text-muted border border-transparent hover:border-tech-border'}`}
                  >{l.emoji} {l.label}</button>
                ))}
              </div>
            </div>

            {/* Timeline + Collab row */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-tech-text-muted mb-2 block uppercase tracking-wider">Timeline</label>
                <div className="flex flex-wrap gap-1.5">
                  <button type="button" onClick={() => setFilterTimeline('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterTimeline === 'all' ? 'bg-tech-cyan/15 text-tech-cyan border border-tech-cyan/30' : 'bg-tech-gray text-tech-text-muted border border-transparent hover:border-tech-border'}`}
                  >All</button>
                  {TIMELINES.filter(t => t.id !== 'custom').map(t => (
                    <button key={t.id} type="button" onClick={() => setFilterTimeline(t.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${filterTimeline === t.id ? 'bg-tech-cyan/15 text-tech-cyan border border-tech-cyan/30' : 'bg-tech-gray text-tech-text-muted border border-transparent hover:border-tech-border'}`}
                    >{t.emoji} {t.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-tech-text-muted mb-2 block uppercase tracking-wider">Collaboration</label>
                <div className="flex flex-wrap gap-1.5">
                  <button type="button" onClick={() => setFilterCollab('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterCollab === 'all' ? 'bg-tech-cyan/15 text-tech-cyan border border-tech-cyan/30' : 'bg-tech-gray text-tech-text-muted border border-transparent hover:border-tech-border'}`}
                  >All</button>
                  {COLLAB_STYLES.map(c => (
                    <button key={c.id} type="button" onClick={() => setFilterCollab(c.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${filterCollab === c.id ? 'bg-tech-cyan/15 text-tech-cyan border border-tech-cyan/30' : 'bg-tech-gray text-tech-text-muted border border-transparent hover:border-tech-border'}`}
                    >{c.emoji} {c.label}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Result count + Sort */}
        {!loading && (
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <p className="text-xs text-tech-text-muted">
              {resultCount} {resultCount === 1 ? 'task' : 'tasks'} found
              {searchQuery.trim() ? ` for "${searchQuery.trim()}"` : ''}
              {activeFilterCount > 0 ? ` (${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} active)` : ''}
            </p>
            {/* Sort toggle */}
            <div className="flex items-center gap-1 p-0.5 rounded-lg bg-tech-gray/60 border border-tech-border/60">
              <button
                type="button"
                onClick={() => setSortBy('latest')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  sortBy === 'latest'
                    ? 'bg-tech-elevated text-tech-text shadow-sm border border-tech-border/40'
                    : 'text-tech-text-muted hover:text-tech-text'
                }`}
              >
                <Sparkles className="w-3 h-3" /> Latest
              </button>
              <button
                type="button"
                onClick={() => setSortBy('trending')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  sortBy === 'trending'
                    ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-300 border border-orange-500/30 shadow-sm'
                    : 'text-tech-text-muted hover:text-tech-text'
                }`}
              >
                <Flame className="w-3 h-3" /> Trending
              </button>
            </div>
          </div>
        )}

        {/* Tasks feed */}
        {loading ? (
          <div className="text-center py-20">
            <Loader className="w-8 h-8 animate-spin text-tech-cyan mx-auto mb-3" />
            <p className="text-sm text-tech-text-muted">Loading tasks...</p>
          </div>
        ) : resultCount === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-tech-border/40 bg-tech-gray/20 max-w-lg mx-auto">
            <TrendingUp className="w-12 h-12 text-tech-text-muted/40 mx-auto mb-3" />
            <p className="text-tech-text font-medium">No tasks found</p>
            <p className="text-sm text-tech-text-muted mt-1">
              {searchQuery.trim() || activeFilterCount > 0 ? 'Try different search or filters' : 'Check back later for new tasks'}
            </p>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="mt-4 px-4 py-2 rounded-lg bg-tech-cyan/10 text-tech-cyan text-xs font-medium border border-tech-cyan/20 hover:bg-tech-cyan/20 transition-all">
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Direct match banner */}
            {directMatchTask && (
              <div className="mb-5 rounded-2xl border border-tech-cyan/40 bg-tech-cyan/5 p-4 shadow-[0_0_24px_rgba(34,211,238,0.08)]">
                <div className="flex items-center gap-2 mb-3">
                  <Fingerprint className="w-4 h-4 text-tech-cyan" />
                  <span className="text-xs font-bold text-tech-cyan uppercase tracking-wider">Direct Match</span>
                  <span className="font-mono text-[11px] text-tech-cyan/70 bg-tech-cyan/10 px-2 py-0.5 rounded-md border border-tech-cyan/20">
                    {(directMatchTask as any).taskCode}
                  </span>
                </div>
                {(() => {
                  const task = directMatchTask;
                  const isOwn = currentUser?.uid === task.userId;
                  const connState = connectionStates[task.id]?.status ?? 'idle';
                  const taskUniverse = TASK_UNIVERSES.find(u => u.id === task.universe);
                  return (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        {taskUniverse && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border mb-1"
                            style={{ borderColor: taskUniverse.color + '40', background: taskUniverse.color + '10', color: taskUniverse.color }}>
                            {taskUniverse.emoji} {taskUniverse.label}
                          </span>
                        )}
                        <p className="font-bold text-tech-text truncate">{task.title}</p>
                        <p className="text-xs text-tech-text-muted line-clamp-1 mt-0.5">{task.goal || task.summary}</p>
                        <p className="text-[10px] text-tech-text-muted/60 mt-1">by {task.userName || 'Anonymous'}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => setSearchParams({ task: task.id })}
                          className="px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5 border border-tech-cyan/40 bg-tech-cyan/10 text-tech-cyan hover:bg-tech-cyan/20 transition-all"
                        >
                          <Eye className="w-4 h-4" /> View Task
                        </button>
                        {currentUser && !isOwn && connState === 'idle' && (
                          <button
                            onClick={() => setConnectionStates((p) => ({ ...p, [task.id]: { status: 'composing' } }))}
                            className="px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5 border border-tech-border bg-tech-gray/40 text-tech-text hover:border-tech-cyan/30 hover:text-tech-cyan transition-all"
                          >
                            <MessageCircle className="w-4 h-4" /> Connect
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTasks.map((task) => {
                const isOwn = currentUser?.uid === task.userId;
                const connState = connectionStates[task.id]?.status ?? 'idle';
                const existingConn = connectionStates[task.id]?.existing;
                const score = task.matchScore ?? 0;
                const taskUniverse = TASK_UNIVERSES.find(u => u.id === task.universe);
                const tools = ((task as any).tools || []) as { name: string }[];
                const taskTags = task.tags || [];

                return (
                  <div
                    key={task.id}
                    className="group flex flex-col rounded-2xl border border-tech-border/40 bg-tech-gray/30 p-5 hover:border-tech-cyan/30 hover:bg-tech-gray/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(34,211,238,0.06)]"
                  >
                    {/* Universe badge + trending badge + time */}
                    <div className="flex items-center justify-between mb-3 gap-1.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {taskUniverse ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border shrink-0"
                            style={{ borderColor: taskUniverse.color + '40', background: taskUniverse.color + '10', color: taskUniverse.color }}>
                            {taskUniverse.emoji} {taskUniverse.label}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-tech-gray text-[10px] text-tech-text-muted border border-tech-border/50 shrink-0">
                            {task.category}
                          </span>
                        )}
                        {((ratingSummaries[task.id]?.onPoint ?? 0) + (ratingSummaries[task.id]?.offTopic ?? 0)) >= 3 && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-400/25 text-[10px] font-bold text-emerald-400 shrink-0" title="On-point score">
                            <Flame className="w-2.5 h-2.5" /> {onPointScore(ratingSummaries[task.id])}%
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-tech-text-muted flex items-center gap-1 shrink-0">
                        <Clock className="w-3 h-3" /> {formatRelativeTime(task.createdAt)}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-base font-bold text-tech-text mb-1 line-clamp-2 leading-snug group-hover:text-tech-cyan transition-colors">
                      {task.title}
                    </h3>
                    {/* Task Code badge */}
                    {(task as any).taskCode && (
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] text-tech-cyan/60 bg-tech-cyan/5 border border-tech-cyan/15 px-2 py-0.5 rounded-md mb-1.5 select-all">
                        <Fingerprint className="w-2.5 h-2.5" />
                        {(task as any).taskCode}
                      </span>
                    )}
                    <p className="text-xs text-tech-text-muted line-clamp-2 mb-3">
                      {task.goal || task.summary}
                    </p>

                    {/* Creator */}
                    <Link
                      to={`/tasker/${task.userId}`}
                      className="inline-flex items-center gap-2 text-xs text-tech-text-muted hover:text-tech-cyan transition-colors mb-3 group/creator"
                    >
                      <div className="w-6 h-6 rounded-full bg-tech-cyan/10 border border-tech-cyan/20 flex items-center justify-center text-[10px] font-bold text-tech-cyan">
                        {(task.userName || '?').charAt(0).toUpperCase()}
                      </div>
                      <span className="group-hover/creator:underline">{task.userName || 'Anonymous'}</span>
                      <ExternalLink className="w-2.5 h-2.5 opacity-0 group-hover/creator:opacity-100 transition-opacity" />
                    </Link>

                    {/* Tools */}
                    {tools.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {tools.slice(0, 4).map((t: any, i: number) => (
                          <span key={i} className="px-2 py-0.5 rounded-md bg-tech-gray text-[10px] text-tech-text-muted border border-tech-border/50">
                            {t.name}
                          </span>
                        ))}
                        {tools.length > 4 && <span className="text-[10px] text-tech-text-muted px-1">+{tools.length - 4}</span>}
                      </div>
                    )}

                    {/* Tags + Match score */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-4">
                      {currentUser && !isOwn && score > 0 && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${scoreColor(score)}`}>
                          <Zap className="w-2.5 h-2.5" /> {score}% match
                        </span>
                      )}
                      {taskTags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="text-[10px] text-tech-cyan/70">#{tag}</span>
                      ))}
                    </div>

                    <div className="flex-1" />

                    {/* Connection UI */}
                    {currentUser && !isOwn && (
                      <div className="mb-3">
                        {connState === 'exists' && existingConn && (
                          <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-tech-gray/60 border border-tech-border/50">
                            {existingConn.status === 'accepted' ? (
                              <><CheckCircle className="w-3.5 h-3.5 text-emerald-400" /><span className="text-emerald-300">Connected</span></>
                            ) : existingConn.status === 'pending' ? (
                              <><Clock className="w-3.5 h-3.5 text-amber-400" /><span className="text-amber-300">Pending</span></>
                            ) : (
                              <><XCircle className="w-3.5 h-3.5 text-red-400" /><span className="text-red-300">Declined</span></>
                            )}
                          </div>
                        )}
                        {connState === 'sent' && (
                          <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-400/20">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /><span className="text-emerald-300">Request sent</span>
                          </div>
                        )}
                        {connState === 'composing' && (
                          <div className="space-y-2">
                            <textarea
                              value={draftMessages[task.id] || ''}
                              onChange={(e) => setDraftMessages((p) => ({ ...p, [task.id]: e.target.value }))}
                              rows={2}
                              placeholder="Introduce yourself..."
                              className="w-full rounded-xl bg-tech-gray border border-tech-border px-3 py-2 text-xs text-tech-text placeholder-tech-text-muted/50 focus:outline-none focus:border-tech-cyan focus:ring-1 focus:ring-tech-cyan/30 resize-none"
                            />
                            <div className="flex gap-2">
                              <button type="button" onClick={() => void handleSendRequest(task)}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                                style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)', color: '#0a0a0a' }}>
                                <Send className="w-3 h-3" /> Send
                              </button>
                              <button type="button" onClick={() => setConnectionStates((p) => ({ ...p, [task.id]: { status: 'idle' } }))}
                                className="px-3 py-1.5 rounded-lg text-xs text-tech-text-muted hover:bg-tech-gray transition">Cancel</button>
                            </div>
                          </div>
                        )}
                        {connState === 'sending' && (
                          <div className="flex items-center justify-center gap-2 px-3 py-2 text-xs text-tech-text-muted">
                            <Loader className="w-3.5 h-3.5 animate-spin" /> Sending...
                          </div>
                        )}
                      </div>
                    )}

                    {/* On-Point vs Off-Topic rating */}
                    <div className="mb-2">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleVote(task.id, 'on_point')}
                          disabled={!currentUser || ratingBusy[task.id]}
                          title={currentUser ? 'On-Point — this task nails it' : 'Sign in to rate'}
                          className={[
                            'used-btn flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-all duration-200 select-none',
                            myVote[task.id] === 'on_point'
                              ? 'used-btn--active bg-emerald-500/15 border-emerald-400/40 text-emerald-400 hover:bg-emerald-500/25'
                              : 'bg-tech-gray/50 border-tech-border/60 text-tech-text-muted hover:border-emerald-400/30 hover:text-emerald-400',
                            (!currentUser || ratingBusy[task.id]) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                          ].join(' ')}
                        >
                          <CheckCircle className={`w-3.5 h-3.5 transition-transform duration-200 ${myVote[task.id] === 'on_point' ? 'scale-110' : ''}`} />
                          <span>On-Point</span>
                          {(ratingSummaries[task.id]?.onPoint ?? 0) > 0 && (
                            <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-black tabular-nums bg-emerald-400/20 text-emerald-300">
                              {ratingSummaries[task.id]?.onPoint}
                            </span>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleVote(task.id, 'off_topic')}
                          disabled={!currentUser || ratingBusy[task.id]}
                          title={currentUser ? 'Off-Topic — misses the mark' : 'Sign in to rate'}
                          className={[
                            'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-all duration-200 select-none',
                            myVote[task.id] === 'off_topic'
                              ? 'bg-rose-500/15 border-rose-400/40 text-rose-400 hover:bg-rose-500/25'
                              : 'bg-tech-gray/50 border-tech-border/60 text-tech-text-muted hover:border-rose-400/30 hover:text-rose-400',
                            (!currentUser || ratingBusy[task.id]) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                          ].join(' ')}
                        >
                          <XCircle className={`w-3.5 h-3.5 transition-transform duration-200 ${myVote[task.id] === 'off_topic' ? 'scale-110' : ''}`} />
                          <span>Off-Topic</span>
                          {(ratingSummaries[task.id]?.offTopic ?? 0) > 0 && (
                            <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-black tabular-nums bg-rose-400/20 text-rose-300">
                              {ratingSummaries[task.id]?.offTopic}
                            </span>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSearchParams({ task: task.id })}
                        className="flex-1 px-3 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-1.5 border border-tech-cyan/30 bg-tech-cyan/5 text-tech-cyan hover:bg-tech-cyan/15"
                      >
                        <Eye className="w-4 h-4" /> View
                      </button>
                      {currentUser && !isOwn && connState === 'idle' && (
                        <button
                          onClick={() => setConnectionStates((p) => ({ ...p, [task.id]: { status: 'composing' } }))}
                          className="px-3 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-1.5 border border-tech-border bg-tech-gray/40 text-tech-text hover:border-tech-cyan/30 hover:text-tech-cyan"
                        >
                          <MessageCircle className="w-4 h-4" />
                          <span className="hidden sm:inline">Connect</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <div className="text-center mt-8">
                <button onClick={loadMore} disabled={loadingMore}
                  className="px-6 py-2.5 rounded-xl border border-tech-border bg-tech-gray/40 text-sm font-medium text-tech-text-muted hover:border-tech-cyan/30 hover:text-tech-cyan transition-all disabled:opacity-50">
                  {loadingMore ? 'Loading...' : 'Load more tasks'}
                </button>
              </div>
            )}
          </>
        )}

        {selectedTaskId && (
          <TaskDetailModal
            taskId={selectedTaskId}
            isOpen={!!selectedTaskId}
            onClose={() => {
              const newParams = new URLSearchParams(searchParams);
              newParams.delete('task');
              newParams.delete('connect');
              setSearchParams(newParams);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default TaskMarketplace;
